import { Link } from 'react-router-dom';
import { Phone, MessageSquare, Mail, Shield, Eye, Lock } from 'lucide-react';
import { siteConfig } from '@/config/site';
import { useLanguage } from '@/context/LanguageContext';

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export function Footer() {
  const { language } = useLanguage();
  const content = siteConfig.content[language].footer;
  const currentYear = new Date().getFullYear();

  const legalLinks = {
    en: {
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      nda: 'Non-Disclosure',
    },
    es: {
      terms: 'Términos de Servicio',
      privacy: 'Política de Privacidad',
      nda: 'No Divulgación',
    },
  };

  return (
    <>
      <footer className="bg-[#07060B] border-t border-white/5 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="relative inline-block mb-3">
                <img 
                  src={siteConfig.brand.logoUrl}
                  alt="Casa de Candy"
                  className="h-16 object-contain"
                  style={{
                    filter: 'drop-shadow(0 0 8px rgba(139, 92, 255, 0.7)) drop-shadow(0 0 15px rgba(255, 77, 166, 0.4))'
                  }}
                />
              </div>
              <p className="text-[#8B5CFF] text-xs mb-2">
                {siteConfig.brand.established}
              </p>
              <p className="text-[#A7A3BA] text-sm leading-relaxed">
                {siteConfig.legalDescription[language]}
              </p>
            </div>

            {/* Quick Contact - Labels Only */}
            <div>
              <h4 className="font-heading font-semibold text-[#F4F2FF] mb-4 text-sm uppercase tracking-wider">
                {language === 'en' ? 'Contact' : 'Contacto'}
              </h4>
              <div className="space-y-3">
                <a
                  href={`tel:${siteConfig.contact.phone}`}
                  className="flex items-center gap-2 text-[#A7A3BA] hover:text-[#FF4DA6] transition-colors text-sm"
                  data-testid="footer-call"
                >
                  <Phone className="w-4 h-4 text-[#FF4DA6]" />
                  <span>Call</span>
                </a>
                <a
                  href={`sms:${siteConfig.contact.phone}`}
                  className="flex items-center gap-2 text-[#A7A3BA] hover:text-[#27D7FF] transition-colors text-sm"
                  data-testid="footer-text"
                >
                  <MessageSquare className="w-4 h-4 text-[#27D7FF]" />
                  <span>Text</span>
                </a>
                <a
                  href={siteConfig.contact.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[#A7A3BA] hover:text-[#25D366] transition-colors text-sm"
                  data-testid="footer-whatsapp"
                >
                  <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
                  <span>WhatsApp</span>
                </a>
                <a
                  href={`mailto:${siteConfig.brand.email}`}
                  className="flex items-center gap-2 text-[#A7A3BA] hover:text-[#8B5CFF] transition-colors text-sm"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 text-[#8B5CFF]" />
                  <span>Email</span>
                </a>
              </div>
            </div>

            {/* Location */}
            <div>
              <h4 className="font-heading font-semibold text-[#F4F2FF] mb-4 text-sm uppercase tracking-wider">
                {siteConfig.content[language].location.title}
              </h4>
              <p className="text-[#A7A3BA] text-sm mb-2">{siteConfig.location.area}</p>
              <p className="text-[#A7A3BA] text-sm mb-2">{siteConfig.location.fullAddress}</p>
              <p className="text-[#8B5CFF] text-xs italic">{siteConfig.location.note}</p>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-[#8B5CFF]/30 to-transparent mb-6" />

          {/* Legal Links - Standalone compliance pages */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
            <Link
              to="/privacy"
              className="flex items-center gap-1.5 text-xs text-[#A7A3BA]/70 hover:text-[#8B5CFF] transition-colors"
              data-testid="footer-privacy-link"
            >
              <Eye className="w-3 h-3" />
              {legalLinks[language].privacy}
            </Link>
            <span className="text-[#A7A3BA]/30">|</span>
            <Link
              to="/terms"
              className="flex items-center gap-1.5 text-xs text-[#A7A3BA]/70 hover:text-[#FF4DA6] transition-colors"
              data-testid="footer-terms-link"
            >
              <Shield className="w-3 h-3" />
              {legalLinks[language].terms}
            </Link>
            <span className="text-[#A7A3BA]/30">|</span>
            <Link
              to="/nda"
              className="flex items-center gap-1.5 text-xs text-[#A7A3BA]/70 hover:text-[#27D7FF] transition-colors"
              data-testid="footer-nda-link"
            >
              <Lock className="w-3 h-3" />
              {legalLinks[language].nda}
            </Link>
          </div>

          {/* Bottom */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[#A7A3BA] text-xs text-center md:text-left">
              {content.disclaimer}
            </p>
            <div className="flex items-center gap-4">
              <p className="text-[#A7A3BA] text-xs">
                © {currentYear} {content.copyright}. All rights reserved.
              </p>
              <a 
                href="https://emergent.sh" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#A7A3BA]/40 text-xs hover:text-[#A7A3BA]/60 transition-colors"
              >
                Website by emergent.sh
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default Footer;
